import React, { useCallback, useMemo, useState, useRef } from "react";
import { CardHeader } from "@remo-co/ui-core/src/components/CardHeader";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Menu } from "@remo-co/ui-core/src/components/Menu";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { IconButtonWithTooltip } from "@remo-co/ui-core/src/components/IconButtonWithTooltip";
import { Edit as EditIcon } from "@remo-co/ui-core/src/icons/Edit";
import { MoreVert as MoreIcon } from "@remo-co/ui-core/src/icons/MoreVert";
import { Link, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useI18n } from "i18n";
import logger from "logging/logger";
import { selectCurrentRoom } from "store/rooms";
import { selectCurrentSpaceId } from "modules/space/redux/selectors";
import useUserStatusActions from "modules/userStatus/hooks/useUserStatusActions";
import { Colors } from "@remo-co/ui-core/theme";
import { EventCard } from "modules/event/components";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isTrialCompleted } from "modules/companyPlanSettings/utils/trial";
import moment from "moment";
import classNames from "classnames";
import { getCurrentTimezone } from "helpers/time/timezone";
import useAuth from "modules/auth/hooks/useAuth";
import useEventAdmin from "modules/event/hooks/useEventAdmin";
import { EVENT_DEFAULT_IMAGE } from "modules/event";
import useEventUtils from "modules/event/hooks/useEventUtils";
import UpgradePlanPopup from "modules/upgradePlan/UpgradePlanPopup";
import { selectUser } from "modules/auth/redux/selectors";
import PerformanceLogger from "helpers/performanceHelper";
import AgendaIcon from "modules/eventAgendaPublic/assets/agenda.svg";
import { IEvent } from "modules/event/types";
import { selectUnlimitedOngoingAndUpcomingEvents } from "modules/event/selectors";
import { Cancel } from "@remo-co/ui-core/src/icons/Cancel";
import { Refresh } from "@remo-co/ui-core/src/icons/Refresh";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";
import { SpaceActionButton } from "../SpaceActionButton";
import {
  CloneEventButton,
  DeleteEventButton,
  EditEventButton,
  EnterEventButton,
  ViewEventButton,
  InviteLink,
} from "..";
import { useStyles } from "./styles";

interface Props {
  event: IEvent;
  editable?: boolean;
  ended?: boolean;
  refetch?: () => void;
}

const EventListItem = ({
  event,
  editable,
  ended,
  refetch,
}: Props): JSX.Element => {
  const styles = useStyles();
  const [upgradeMessage, setUpgradeMessage] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const eventCardRef = useRef<null | HTMLDivElement>(null);
  const { isManager, isEventHelper } = useAuth();
  const user = useSelector(selectUser);
  const { push } = useHistory();
  const userId = user ? user.id : null;
  const spaceId = useSelector(selectCurrentSpaceId);
  const currentRoom = useSelector(selectCurrentRoom);
  const roomId = currentRoom ? currentRoom.id : null;
  const timezone = getCurrentTimezone();
  const { amIAdminForEvent, reopenEvent, stopEvent } = useEventAdmin();
  const { isAllowedToViewEvent } = useEventUtils();
  const { removeUserFromSpace } = useUserStatusActions();
  const { t } = useI18n(["common", "company", "event", "manageEvent"]);
  const { myCompanyPlan, unlimitedEventsSettings } = useCompanyPlanSettings();
  const dispatch = useAppDispatch();
  const ongoingUnlimitedEvent = useSelector(
    selectUnlimitedOngoingAndUpcomingEvents,
  );

  const {
    id: eventId,
    code: eventCode,
    logoURL,
    name,
    shortDescription,
    startTime,
    endTime,
    capacity,
    numFloors,
    numSponsors,
    numAttendees,
    isPrivate,
    eventType,
    isUnlimitedEvent,
  } = event;
  const imageURL = logoURL || EVENT_DEFAULT_IMAGE;
  const eventSummaryURL = `/event/settings/${eventId}`;

  const isPastEvent = useMemo(
    () => moment(endTime).isBefore(Date.now()),
    [endTime],
  );

  const isUpcomingEvent = useMemo(
    () => moment(startTime).isAfter(Date.now()),
    [startTime],
  );

  const eventLink = useMemo(
    () =>
      `/e/${eventCode}${
        user &&
        !amIAdminForEvent(eventId, user.id) &&
        (isUpcomingEvent || isPastEvent)
          ? "/register"
          : ""
      }`,
    [amIAdminForEvent, eventCode, eventId, isPastEvent, isUpcomingEvent, user],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleOptionsClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();

      if (editable && isTrialCompleted(myCompanyPlan)) {
        setUpgradeMessage(t("company:trial.expired.message"));

        return;
      }

      setAnchorEl(e.currentTarget);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editable, myCompanyPlan],
  );

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.stopPropagation();
      push(`${e.currentTarget.pathname}${e.currentTarget.search}`);
      handleClose();
    },
    [push, handleClose],
  );

  const handleEditClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (editable && isTrialCompleted(myCompanyPlan)) {
        setUpgradeMessage(t("company:trial.expired.message"));

        return;
      }
      handleLinkClick(e);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleLinkClick],
  );

  const goToEvent = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation();

      if (editable && isTrialCompleted(myCompanyPlan)) {
        setUpgradeMessage(t("company:trial.expired.message"));
        logger.info(
          `[EventListItem] Trial expired while trying to enter event ${eventId}.`,
        );

        return;
      }
      handleClose();
      const isAdmin = user ? amIAdminForEvent(eventId, user.id) : false;

      if (!isAllowedToViewEvent(event, isAdmin, isManager)) {
        logger.info(
          `[EventListItem] User ${user?.id} is not allowed to view event ${eventId}.`,
        );

        if (userId) {
          removeUserFromSpace({
            spaceId,
            roomId: roomId || "",
            userId,
          });
        }
      }

      PerformanceLogger.start("EventLoad");
      push(eventLink);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventId, user, roomId, userId],
  );

  const handleReopenSpace = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (
        unlimitedEventsSettings &&
        ongoingUnlimitedEvent.length >= unlimitedEventsSettings?.concurrent
      ) {
        dispatch(
          addDialogNotification({
            title: t("event:reopen.event.error"),
            hideCloseButton: true,
            hideDismissButton: true,
            content: (
              <div
                className={styles.reopenDialogContent}
                data-testid="reopen-content-dialog"
              >
                <div>
                  <Typography variant="body1">
                    {t("event:reopen.dialog.content")}
                  </Typography>
                </div>
                <div className={styles.activeSpaceName}>
                  <Typography variant="body1">
                    {`${t("event:active.space")}: ${ongoingUnlimitedEvent
                      .map((space) => space?.name)
                      .filter(Boolean)
                      .join(",")}`}
                  </Typography>
                </div>
              </div>
            ),

            confirmText: t("common:button.ok"),
          }),
        );
      } else {
        dispatch(
          addDialogNotification({
            hideCloseButton: true,
            title: (
              <Typography variant="h4">{t("event:reopen.space")}</Typography>
            ),
            message: t("event:reopen.space.message"),
            onConfirm: async () => {
              await reopenEvent(eventId);
              refetch?.();
            },
            confirmText: t("yes"),
            dismissText: t("no"),
          }),
        );
      }
    },
    [
      unlimitedEventsSettings,
      ongoingUnlimitedEvent,
      dispatch,
      styles.reopenDialogContent,
      styles.activeSpaceName,
      t,
      reopenEvent,
      eventId,
      refetch,
    ],
  );

  const handleCloseSpace = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      dispatch(
        addDialogNotification({
          hideCloseButton: true,
          title: <Typography variant="h4">{t("event:close.space")}</Typography>,
          message: t("event:close.space.message"),
          onConfirm: async () => {
            await stopEvent(eventId);
            refetch?.();
          },
          confirmText: t("yes"),
          dismissText: t("no"),
        }),
      );
    },
    [dispatch, t, stopEvent, eventId, refetch],
  );

  return (
    <div className={classNames(styles.eventListItemContainer)}>
      <Link
        onClick={goToEvent}
        to={eventLink}
        style={{ textDecoration: "none" }}
        data-testid="event-list-item"
      >
        <EventCard
          data={{
            imageURL,
            name: name ?? "",
            shortDescription,
            startTime: startTime ?? null,
            endTime: endTime ?? null,
            timezone,
            capacity,
            numFloors,
            numSponsors,
            numAttendees,
            isUnlimitedEvent,
          }}
          ref={eventCardRef}
        >
          <CardHeader
            action={
              <>
                <div className={styles.menu}>
                  {editable ? (
                    <>
                      {!ended && !isPastEvent && (
                        <IconButtonWithTooltip
                          title={t("event:event.agenda")}
                          onClick={handleLinkClick}
                          component={Link}
                          to={`/event/agenda/${eventId}`}
                          id={`ev-${eventId}-agenda-btn`}
                          className={classNames(styles.menuItem, styles.white)}
                          disabled={isTrialCompleted(myCompanyPlan)}
                          data-testid="event-agenda-button"
                        >
                          <img src={AgendaIcon} alt={t("event:event.agenda")} />
                        </IconButtonWithTooltip>
                      )}
                      {isUnlimitedEvent && (
                        <div className={styles.privateEvent}>
                          <Typography className={styles.privateEventText}>
                            {t("event:24.7.space")}
                          </Typography>
                        </div>
                      )}
                      {isPrivate && !isUnlimitedEvent && (
                        <div
                          className={styles.privateEvent}
                          data-testid="private-event-badge"
                        >
                          <Typography variant="h5">
                            {t("event:private.event")}
                          </Typography>
                        </div>
                      )}

                      <div style={{ flex: 1 }} />
                      <IconButtonWithTooltip
                        title={t("event:event.edit")}
                        onClick={handleEditClick}
                        component={Link}
                        to={eventSummaryURL}
                        id={`ev-${eventId}-edit-btn`}
                        hoverColor={Colors.WHITE}
                        className={classNames(
                          styles.menuItem,
                          styles.editButton,
                        )}
                        data-testid="event-list-item-edit-button"
                      >
                        <EditIcon />
                      </IconButtonWithTooltip>
                    </>
                  ) : (
                    <div style={{ flex: 1 }} />
                  )}

                  <IconButton
                    className={styles.menuItem}
                    aria-label={t("event:options")}
                    onClick={handleOptionsClick}
                    data-testid="show-event-item-options"
                    size="large"
                  >
                    <MoreIcon />
                  </IconButton>
                </div>
                {isPrivate && isUnlimitedEvent && (
                  <div
                    className={`${styles.privateEvent} ${styles.unlimitedPrivateEvent}`}
                    data-testid="private-event-badge"
                  >
                    <Typography>{t("event:private.event")}</Typography>
                  </div>
                )}
              </>
            }
          />
        </EventCard>
      </Link>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className={styles.menuList}
      >
        {((user && amIAdminForEvent(eventId, user.id)) ||
          (!isUpcomingEvent && !isPastEvent)) && (
          <EnterEventButton goToEvent={goToEvent} code={eventCode} />
        )}
        {editable && (
          <EditEventButton
            eventId={eventId}
            handleEditClick={handleEditClick}
          />
        )}
        {editable && !isUnlimitedEvent && (
          <CloneEventButton
            handleClose={handleClose}
            eventId={eventId}
            className={styles.listItem}
            eventType={eventType}
          />
        )}
        {eventCode && (
          <ViewEventButton eventCode={eventCode} handleClose={handleClose} />
        )}
        {editable && <InviteLink event={event} className={styles.listItem} />}
        {isUnlimitedEvent && !isEventHelper && !isPastEvent && editable && (
          <SpaceActionButton
            className={styles.listItem}
            handleAction={handleCloseSpace}
            icon={<Cancel fontSize="small" color="secondary" />}
            actionTitle={t("event:close.space")}
          />
        )}
        {isPastEvent && !isEventHelper && isUnlimitedEvent && editable && (
          <SpaceActionButton
            className={styles.listItem}
            handleAction={handleReopenSpace}
            icon={<Refresh fontSize="small" color="secondary" />}
            actionTitle={t("event:reopen.space")}
          />
        )}
        {/** dont allow event helper to delete an event */}
        <DeleteEventButton
          eventId={event.id}
          className={styles.listItem}
          isEventHelper={isEventHelper}
          isEditable={editable}
          refetch={refetch}
        />
      </Menu>
      <UpgradePlanPopup
        upgradeMessage={upgradeMessage}
        onClose={() => setUpgradeMessage(null)}
      />
    </div>
  );
};

export default EventListItem;
